import { router } from "@inertiajs/react"
import { useMemo } from "react"

import {
  Kanban,
  KanbanBoard,
  KanbanColumn,
  KanbanColumnContent,
  KanbanColumnHeader,
  KanbanItem,
  KanbanOverlay,
} from "@/components/ui/kanban"
import { statusDot } from "@/components/steel/status"
import { formatCurrency } from "@/lib/utils"

export type BidStatus = "draft" | "sent" | "accepted" | "rejected" | "converted"

export type BidCard = {
  id: string
  name: string | null
  status: BidStatus
  revision: number
  convertedProjectId?: string | null
  contact: { clientName?: string; companyName?: string }
  calculated: { grandTotal: number; totalSqFt?: number; price?: number; profit?: number }
}

const BID_COLUMNS: { id: BidStatus; label: string }[] = [
  { id: "draft", label: "Draft" },
  { id: "sent", label: "Sent" },
  { id: "accepted", label: "Accepted" },
  { id: "rejected", label: "Rejected" },
  { id: "converted", label: "Converted" },
]

export function BidsKanban({ bids }: { bids: BidCard[] }) {
  const columns = useMemo(() => {
    const result = Object.fromEntries(BID_COLUMNS.map((c) => [c.id, [] as BidCard[]])) as Record<
      BidStatus,
      BidCard[]
    >
    for (const bid of bids) result[bid.status]?.push(bid)
    return result
  }, [bids])

  const handleValueChange = (next: Record<string, BidCard[]>) => {
    for (const [status, columnBids] of Object.entries(next)) {
      for (const bid of columnBids) {
        if (bid.status !== status) {
          router.patch(`/bids/${bid.id}`, { status }, { preserveScroll: true })
        }
      }
    }
  }

  return (
    <Kanban
      value={columns}
      onValueChange={handleValueChange}
      getItemValue={(bid) => bid.id}
      className="flex gap-px bg-border"
    >
      <KanbanBoard className="gap-px overflow-x-auto border-b border-border bg-border pb-0">
        {BID_COLUMNS.map((column) => {
          const total = columns[column.id].reduce((sum, b) => sum + (b.calculated.price ?? 0), 0)

          return (
            <KanbanColumn
              key={column.id}
              value={column.id}
              className="w-[248px] min-h-[calc(100vh-53px)] rounded-none border-0 bg-background px-3 py-3.5"
            >
              <KanbanColumnHeader className="flex items-center justify-between border-0 p-0 pb-2.5">
                <span className="flex items-center gap-1.5 text-label font-semibold">
                  <span
                    className="h-[5px] w-[5px] rounded-full"
                    style={{ background: statusDot(column.id) }}
                  />
                  {column.label}
                </span>
                <span className="num text-meta text-muted-foreground">{formatCurrency(total)}</span>
              </KanbanColumnHeader>

              <KanbanColumnContent value={column.id} className="gap-2 p-0">
                {columns[column.id].map((bid) => {
                  const sqft = bid.calculated.totalSqFt ?? 0
                  const price = bid.calculated.price ?? 0

                  return (
                    <KanbanItem
                      key={bid.id}
                      value={bid.id}
                      className="rounded-lg border border-border bg-card px-3 py-2.5 shadow-none hover:bg-secondary"
                      onClick={() => router.visit(`/bids/${bid.id}/edit`)}
                    >
                      <p className="mb-1.5 text-body font-medium leading-snug">
                        {bid.name || "Untitled bid"}
                      </p>
                      <p className="mb-2.5 text-meta text-muted-foreground">
                        {bid.contact.companyName || bid.contact.clientName || "No client"}
                      </p>
                      <div className="flex items-baseline justify-between">
                        <span className="num text-body font-medium text-whisper">
                          {formatCurrency(price)}
                        </span>
                        <span className="num text-meta text-muted-foreground">
                          {sqft > 0 ? `$${(price / sqft).toFixed(2)}/ft²` : "—"}
                        </span>
                      </div>
                    </KanbanItem>
                  )
                })}
              </KanbanColumnContent>
            </KanbanColumn>
          )
        })}
      </KanbanBoard>
      <KanbanOverlay />
    </Kanban>
  )
}
